import { useMytaverseEvent } from '@core/dashboard/providers';
import { isMapFeatureFlagEnabled } from '@helpers/events';

import { useMapEditorCatalog } from './useMapEditorCatalog';

/**
 * Whether this map has the map editor at all.
 *
 * The room kit is the gate. A kit is emitted by the build itself (the MytaMapEditor GameCast
 * metadata provider, under `plugins.mapEditor`) and stored on that build's own record, so a build
 * that ships no kit has no rooms to offer and no geometry to spawn — `useMapEditorCatalog` reports
 * that as `source: 'fallback'`, with no elements at all. Since MV-2004 that answer is final: the
 * map-level slot it used to fall back to is no longer read, because it described some other build.
 *
 * Gating on the kit rather than on a setting makes this self-maintaining: a map gains the map editor
 * by shipping one, and there is no second place to keep in step with what the build actually
 * contains. Before this, every surface was ungated or defaulted on, so the Layout panel appeared on
 * every map offering the test map's galleries and corridors — rooms that map cannot build.
 *
 * The SPAWN_LAYOUT flag still applies on top, which is the job it is good at: switching the feature
 * off for a map that does ship a kit. It is deliberately not the gate on its own —
 * `isMapFeatureFlagEnabled` fails open, and it is shared with the spot and trigger teleport actions,
 * so making it fail closed would take those down with it.
 *
 * @returns True when every map-editor surface should render; false hides them outright — no
 *          placeholder and no empty state, because a map without a kit has nothing to explain.
 */
// The flag slug the map editor is gated on, which is NOT the `SPAWN_LAYOUT` message action even
// though it is the same string. They collide by history: the flag was named after the first action
// the feature sent, and it now gates every map-editor action including the finish preview — so
// switching it off to stop layouts being spawned also silently switches off re-finishing a surface.
//
// Written down here because the collision has already cost two codebases an afternoon of hunting.
// UE holds the same slug as a bare literal in MytaMapEditorSubsystem.cpp and reads it from a
// different place: a flat, client-wide table fed by the `FEATURE_FLAGS` websocket message, where the
// hub reads a per-map record (`mapFeatureFlags[mapId][slug]`). Nothing reconciles the two.
export const MAP_EDITOR_FEATURE_FLAG = 'SPAWN_LAYOUT';

export const useCanUseMapEditor = (): boolean => {
  const { currentRoom, currentEvent } = useMytaverseEvent();
  const { source } = useMapEditorCatalog();

  return (
    source === 'build' &&
    isMapFeatureFlagEnabled(
      currentEvent,
      currentRoom?.map,
      MAP_EDITOR_FEATURE_FLAG,
    )
  );
};
